import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Search, Trash2, Share2, Phone, Check, Printer, X, Plus, Activity } from 'lucide-react';
import { toast } from 'react-toastify';
import { useListTreatmentTypesQuery } from '../../api/treatmentApi';
import { useListProcedureCatalogQuery } from '../../api/procedureCatalogApi';
import { useCreateInvoiceMutation, useAddPaymentMutation, usePeekNextInvoiceNumberQuery, useLazyFindInvoiceByNumberQuery, useLazyDownloadInvoiceQuery } from '../../api/billingApi';
import { useGetPatientQuery, useGetPatientOverviewQuery } from '../../api/patientsApi';
import { useListSessionsQuery } from '../../api/sessionsApi';
import { usePatientSessionSummaryQuery } from '../../api/sessionPackagesApi';
import { apiErrorMessage } from '../../utils/apiError';
import ApiErrorBanner from '../../components/ApiErrorBanner';
import { doctorDisplayName } from './doctorColors';

const PAYMENT_METHODS = [
  { value: 'Cash',         label: 'Cash' },
  { value: 'Card',         label: 'Card' },
  { value: 'Upi',          label: 'UPI' },
  { value: 'BankTransfer', label: 'Bank Transfer' },
  { value: 'Online',       label: 'Online' },
];

const DEFAULT_METHOD_KEY = 'healing.payment.defaultMethod';

// Marketing attribution options (value = backend MarketingSource enum name).
// Exported so FirstApptAttributionModal (the No Show/Cancelled counterpart of this section) uses
// the exact same list.
export const MARKETING_SOURCES = [
  { value: 'Google', label: 'Google' },
  { value: 'Facebook', label: 'Facebook' },
  { value: 'Instagram', label: 'Instagram' },
  { value: 'WalkIn', label: 'Walk-In' },
  { value: 'Reference', label: 'Reference' },
];

const fmtINR = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v ?? 0);
const fmtAppt = (d) => {
  const date = new Date(d);
  const day = date.getDate();
  const suffix = (day % 10 === 1 && day !== 11) ? 'st' : (day % 10 === 2 && day !== 12) ? 'nd' : (day % 10 === 3 && day !== 13) ? 'rd' : 'th';
  const month = date.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', month: 'short' });
  const time = date.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' }).toLowerCase();
  return `${day}${suffix} ${month}, ${time}`;
};

function fmtDuration(mins) {
  const m = Math.max(0, Number(mins) || 0);
  const h = Math.floor(m / 60);
  const r = m % 60;
  const parts = [];
  if (h) parts.push(`${h} hour${h > 1 ? 's' : ''}`);
  if (r) parts.push(`${r} min`);
  return parts.length ? parts.join(' ') : '0 min';
}

export default function CollectPaymentModal({ patient, appointment, doctorName, onClose }) {
  // We rely on prop patient — if only an id was passed, AppointmentsPage fetches it
  const { data: patientFresh } = useGetPatientQuery(patient?.id, { skip: !patient?.id || !!patient?.fullName });
  const p = patientFresh ?? patient;

  const [step, setStep] = useState(1);            // 1 = build, 2 = print/done
  const [items, setItems] = useState([]);          // [{ tempId, description, treatmentTypeId, quantity, unitPrice, taxRate }]
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(true);
  const [paidInvoice, setPaidInvoice] = useState(null);

  const [advance, setAdvance] = useState('');
  const [method, setMethod] = useState(() => localStorage.getItem(DEFAULT_METHOD_KEY) || 'Cash');
  const [transactionRef, setTransactionRef] = useState('');
  const [serverError, setServerError] = useState(null);

  // Discount: either a percentage of subtotal or a fixed ₹ amount.
  const [discountMode, setDiscountMode] = useState('none'); // 'none' | 'percent' | 'amount'
  const [discountValue, setDiscountValue] = useState('');

  // First-billing marketing attribution (mandatory on the patient's very first invoice).
  const [marketingSource, setMarketingSource] = useState('');
  const [referralName, setReferralName] = useState('');

  // Close the catalog dropdown when clicking outside it or pressing Escape
  const searchWrapRef = useRef(null);
  useEffect(() => {
    const onDown = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setShowDropdown(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const { data: treatmentTypes = [] } = useListTreatmentTypesQuery();
  const { data: procedureCatalog = [] } = useListProcedureCatalogQuery(true);
  const { data: nextInvoiceNumber } = usePeekNextInvoiceNumberQuery(undefined);
  const [createInvoice, { isLoading: creating }] = useCreateInvoiceMutation();
  const [addPayment, { isLoading: paying }] = useAddPaymentMutation();
  const [triggerDownloadInvoice] = useLazyDownloadInvoiceQuery();
  const [printing, setPrinting] = useState(false);

  // Invoice number: pre-filled with the server's preview, editable via the "edit" link so an
  // operator can match an existing physical book series. Live-checked for a clash.
  const [editingNumber, setEditingNumber] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [numberTouched, setNumberTouched] = useState(false);
  useEffect(() => {
    if (!numberTouched && nextInvoiceNumber) setInvoiceNumber(nextInvoiceNumber);
  }, [nextInvoiceNumber, numberTouched]);
  const [triggerFindByNumber] = useLazyFindInvoiceByNumberQuery();
  const [numberClash, setNumberClash] = useState(null);
  useEffect(() => {
    const n = invoiceNumber.trim();
    if (!n) { setNumberClash(null); return; }
    const t = setTimeout(async () => {
      try { setNumberClash((await triggerFindByNumber(n).unwrap()) || null); }
      catch { setNumberClash(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [invoiceNumber, triggerFindByNumber]);

  // Current session balance for this patient (shown before processing payment)
  const { data: sessionSummary } = usePatientSessionSummaryQuery(p?.id, { skip: !p?.id });

  // Patient overview tells us whether this is their FIRST invoice (→ enforce marketing attribution).
  const { data: overview } = useGetPatientOverviewQuery(p?.id, { skip: !p?.id });
  const isFirstInvoice = overview != null && (overview.invoices?.length ?? 0) === 0;
  const attributionError = isFirstInvoice
    ? (!marketingSource
        ? 'Select how this patient found us.'
        : (marketingSource === 'Reference' && !referralName.trim() ? 'Enter the referral name.' : null))
    : null;

  // Patient's recent completed sessions (last 30 days, max 25) — to quick-bill
  const { data: recentSessions } = useListSessionsQuery(
    { patientId: p?.id, status: 'Completed', pageSize: 25, page: 1 },
    { skip: !p?.id }
  );

  // Track which session IDs are already added so we don't double-add
  const addedSessionIds = useMemo(
    () => new Set(items.filter((it) => it.sessionId).map((it) => it.sessionId)),
    [items]
  );

  // Same admin-managed Settings → "Procedure & Pricing Management" catalog used in the
  // billing register's Add Billing form — selecting one here seeds description/rate the
  // same way, and the rate stays a normal editable input.
  const filteredCatalog = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return procedureCatalog;
    return procedureCatalog.filter((p) => p.name.toLowerCase().includes(s));
  }, [procedureCatalog, search]);

  const subtotal = useMemo(
    () => items.reduce((sum, it) => sum + (Number(it.unitPrice) || 0) * (Number(it.quantity) || 0), 0),
    [items]
  );
  const tax = useMemo(
    () => items.reduce((sum, it) => sum + (Number(it.unitPrice) || 0) * (Number(it.quantity) || 0) * (Number(it.taxRate) || 0) / 100, 0),
    [items]
  );
  // Resolve discount to a concrete ₹ figure (clamped to the pre-discount total).
  const discountAmount = useMemo(() => {
    const v = Number(discountValue) || 0;
    if (v <= 0) return 0;
    const raw = discountMode === 'percent' ? subtotal * v / 100 : (discountMode === 'amount' ? v : 0);
    return Math.min(Math.max(0, raw), subtotal + tax);
  }, [discountMode, discountValue, subtotal, tax]);

  const grandTotal = Math.max(0, subtotal + tax - discountAmount);

  // Advance may legitimately be 0 — the invoice is created either way; payment is just optional.
  const advanceNum = Number(advance) || 0;
  const advanceError = advanceNum < 0
    ? 'Cannot be negative.'
    : (advanceNum > grandTotal + 0.01 ? `Cannot exceed total ${fmtINR(grandTotal)}` : null);
  const canAccept = items.length > 0 && !advanceError && !attributionError && !numberClash && !creating && !paying;

  // "Payment Received" tracks the LIVE invoice total: it re-syncs every time procedures are
  // added/removed or their amounts change — not a one-shot snapshot of the first item. The
  // binding only lets go once the operator edits the field themselves (e.g. a partial payment
  // or a deliberate 0 to defer), after which their value is respected.
  const [advanceTouched, setAdvanceTouched] = useState(false);
  useEffect(() => {
    if (!advanceTouched) {
      setAdvance(grandTotal > 0 ? String(grandTotal) : '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grandTotal, advanceTouched]);

  const addProcedureCatalogItem = (p) => {
    setItems((prev) => [
      ...prev,
      {
        tempId: Date.now() + Math.random(),
        description: p.name,
        // Send the catalog entry's treatment type so the billed line has a durable service
        // identity. Without it the backend falls back to resolving one from the description —
        // correct either way, but passing it avoids relying on the text matching.
        treatmentTypeId: p.treatmentTypeId ?? null,
        quantity: 1,
        unitPrice: Number(p.baseCost) || 0,
        taxRate: 0,
      },
    ]);
    setSearch('');
  };

  const addCustomItem = () => {
    if (!search.trim()) return;
    setItems((prev) => [
      ...prev,
      {
        tempId: Date.now() + Math.random(),
        description: search.trim(),
        treatmentTypeId: null,
        quantity: 1,
        unitPrice: 0,
        taxRate: 0,
      },
    ]);
    setSearch('');
  };

  const addSessionAsItem = (s) => {
    // Already added?
    if (addedSessionIds.has(s.id)) return;
    // Pull price from the session's treatment type catalog entry (fallback 0)
    const tt = treatmentTypes.find((t) => t.id === s.treatmentTypeId);
    const price = Number(tt?.defaultPrice) || 0;
    const desc = `${s.treatmentTypeName || 'Session'} · ${new Date(s.scheduledAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}`;
    setItems((prev) => [
      ...prev,
      {
        tempId: Date.now() + Math.random(),
        description: desc,
        treatmentTypeId: s.treatmentTypeId ?? null,
        sessionId: s.id,
        quantity: 1,
        unitPrice: price,
        taxRate: 0,
      },
    ]);
  };

  const patchItem = (id, patch) => {
    setItems((prev) => prev.map((it) => (it.tempId === id ? { ...it, ...patch } : it)));
  };
  const removeItem = (id) => setItems((prev) => prev.filter((it) => it.tempId !== id));

  const handleSetDefault = () => {
    localStorage.setItem(DEFAULT_METHOD_KEY, method);
    toast.success(`${method} set as default payment method`);
  };

  const handleAccept = async () => {
    if (!canAccept) return;
    setServerError(null);
    try {
      // 1. Create invoice
      const inv = await createInvoice({
        patientId: p.id,
        treatmentPlanId: null,
        dueAt: null,
        invoiceNumber: invoiceNumber.trim() || null,
        discountAmount: discountMode === 'amount' ? (Number(discountValue) || 0) : 0,
        discountPercent: discountMode === 'percent' ? (Number(discountValue) || 0) : 0,
        // First-billing attribution (server enforces requirement on the patient's first invoice).
        marketingSource: isFirstInvoice && marketingSource ? marketingSource : null,
        referralName: isFirstInvoice && marketingSource === 'Reference' ? referralName.trim() : null,
        notes: appointment
          ? `From appointment ${appointment.id} on ${new Date(appointment.scheduledAt).toISOString()}`
          : null,
        items: items.map((it) => ({
          description: it.description,
          treatmentTypeId: it.treatmentTypeId,
          quantity: Number(it.quantity) || 1,
          unitPrice: Number(it.unitPrice) || 0,
          taxRate: Number(it.taxRate) || 0,
        })),
      }).unwrap();

      // 2. Add payment (advance) only if > 0
      if (advanceNum > 0) {
        await addPayment({
          id: inv.id,
          amount: advanceNum,
          method,
          transactionReference: transactionRef || null,
          notes: null,
        }).unwrap();
      }

      // The server now derives the session-package pool directly from these same invoice items
      // (single source of truth — see InvoiceService.CreateAsync), so no separate "sessions
      // purchased" step is needed here.

      setPaidInvoice(inv);
      setStep(2);
      toast.success(`Invoice ${inv.invoiceNumber} created · Payment ${fmtINR(advanceNum)} recorded`);
    } catch (e) {
      const msg = apiErrorMessage(e, 'Could not save invoice / payment');
      setServerError(msg);
      toast.error(msg);
    }
  };

  // The collected-payment "receipt" must be the exact same Invoice PDF generated by
  // InvoicePdfService (logo, Blue/Green/White theme, A4-fit) — not a window.print() of this
  // on-screen success card. So we fetch the real PDF and open it in a new tab, where the
  // browser's native PDF viewer lets the operator print/save the canonical document.
  const handlePrintReceipt = async () => {
    if (!paidInvoice?.id || printing) return;
    setPrinting(true);
    try {
      const blob = await triggerDownloadInvoice(paidInvoice.id).unwrap();
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (!win) {
        // Popup blocked — fall back to a direct download so the receipt is still obtainable.
        const a = document.createElement('a');
        a.href = url;
        a.download = `Invoice_${String(paidInvoice.invoiceNumber || 'INV').replace(/[^a-zA-Z0-9]+/g, '-')}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not generate the invoice/receipt PDF'));
    } finally {
      setPrinting(false);
    }
  };

  const initials = (p?.fullName ?? 'P').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="pay-overlay" onClick={onClose}>
      <div className="pay-modal" onClick={(e) => e.stopPropagation()}>
        {/* Stepper */}
        <div className="pay-stepper">
          <div className={`pay-step ${step === 1 ? 'active' : 'done'}`}>
            <div className="pay-step-dot">{step > 1 && <Check size={12} />}</div>
            <div className="pay-step-label">Invoice &amp; Receipt</div>
          </div>
          <div className={`pay-step-line ${step > 1 ? 'done' : ''}`} />
          <div className={`pay-step ${step === 2 ? 'active' : ''}`}>
            <div className="pay-step-dot" />
            <div className="pay-step-label">Print Invoice &amp; Receipt</div>
          </div>
        </div>

        {step === 1 && (
          <>
            {/* Patient header */}
            <div className="pay-patient">
              <div className="pay-patient-avatar">{initials}</div>
              <div>
                <div className="pay-patient-name">
                  {p?.fullName ?? 'Patient'}
                  <span className="muted-id">({p?.patientCode ?? '—'})</span>
                </div>
              </div>
              <div className="pay-patient-spacer" />
              {sessionSummary && (
                <div
                  className="pay-patient-sessions"
                  title="Patient's current session balance"
                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: 'var(--text-secondary)', marginRight: 14 }}
                >
                  <Activity size={14} style={{ color: 'var(--blue-700)' }} />
                  <span><strong>{sessionSummary.totalRemaining ?? 0}</strong> sessions left</span>
                  <span style={{ color: 'var(--text-muted)' }}>
                    ({sessionSummary.totalCompleted ?? 0}/{sessionSummary.totalPurchased ?? 0} used)
                  </span>
                </div>
              )}
              <div className="pay-patient-contact">
                <div className="pay-patient-contact-row">
                  <Phone size={14} /> {p?.phoneNumber ?? '—'}
                </div>
              </div>
            </div>

            {/* Appointment context */}
            {appointment && (
              <div className="pay-appt-row">
                Appointment with <strong>{doctorDisplayName(doctorName ?? appointment.assignedUserName)}</strong> on{' '}
                <strong>{fmtAppt(appointment.scheduledAt)}</strong> for{' '}
                <strong>{fmtDuration(appointment.durationMinutes ?? 30)}</strong>
              </div>
            )}

            {serverError && (
              <div style={{ padding: '0 28px' }}>
                <ApiErrorBanner message={serverError} onDismiss={() => setServerError(null)} />
              </div>
            )}

            {/* Invoice section */}
            <div className="pay-invoice">
              <div className="pay-invoice-head">
                <strong>Invoice</strong>
                {editingNumber ? (
                  <>
                    <input
                      className="form-control td-mono"
                      style={{ width: 180, height: 26, fontSize: '0.8rem', borderColor: numberClash ? 'var(--red-500)' : undefined }}
                      autoFocus
                      value={invoiceNumber}
                      placeholder={nextInvoiceNumber || 'Auto-generated'}
                      onChange={(e) => { setInvoiceNumber(e.target.value); setNumberTouched(true); }}
                      onBlur={() => setEditingNumber(false)}
                    />
                    {numberClash && (
                      <span className="pay-meta" style={{ color: 'var(--red-700)' }}>
                        Already used by {numberClash.patientName || 'a patient'} on {new Date(numberClash.issuedAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <span className="pay-meta">(Invoice No. will be {invoiceNumber || nextInvoiceNumber || 'auto generated'})</span>
                    <span className="pay-link" onClick={() => setEditingNumber(true)}>edit</span>
                  </>
                )}
                <span className="pay-share" title="Share invoice via WhatsApp / Email"><Share2 size={14} /> Share On</span>
              </div>

              <div className="pay-invoice-body">
                {/* Left: search + dropdown */}
                <div className="pay-search-wrap" ref={searchWrapRef}>
                  <div className="pay-search">
                    <input
                      type="text"
                      placeholder="Search and add items"
                      value={search}
                      onChange={(e) => { setSearch(e.target.value); setShowDropdown(true); }}
                      onFocus={() => setShowDropdown(true)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomItem(); } }}
                    />
                    <Search size={14} className="pay-search-icon" />
                  </div>

                  {showDropdown && (
                    <div className="pay-search-dropdown">
                      {/* Settings → Procedure & Pricing Management catalog — the single source of
                          truth for procedures now; the old hardcoded Treatment-Type list is gone. */}
                      <div className="pay-search-group-label">Procedures</div>
                      {filteredCatalog.map((p) => (
                        <div key={p.id} className="pay-search-item" onClick={() => addProcedureCatalogItem(p)}>
                          <div className="pay-search-item-name">{p.name}</div>
                          <div className="pay-search-item-price">{fmtINR(p.baseCost)}</div>
                        </div>
                      ))}
                      {filteredCatalog.length === 0 && (
                        <div className="pay-search-item" style={{ cursor: 'default' }}>
                          <div className="pay-search-item-name" style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                            No procedures in catalog yet. Use Settings → Procedure &amp; Pricing Management to add some.
                          </div>
                        </div>
                      )}

                      {search.trim() && !filteredCatalog.some((p) => p.name.toLowerCase() === search.trim().toLowerCase()) && (
                        <div className="pay-search-add" onClick={addCustomItem}>
                          + Add &ldquo;{search.trim()}&rdquo; as custom item
                        </div>
                      )}

                      {/* Patient's recent completed sessions — quick-bill */}
                      {(recentSessions?.items?.length ?? 0) > 0 && (
                        <>
                          <div className="pay-search-group-label" style={{ marginTop: 4 }}>
                            <Activity size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                            Patient's recent sessions
                          </div>
                          {recentSessions.items.slice(0, 8).map((s) => {
                            const tt = treatmentTypes.find((t) => t.id === s.treatmentTypeId);
                            const price = Number(tt?.defaultPrice) || 0;
                            const already = addedSessionIds.has(s.id);
                            return (
                              <div
                                key={s.id}
                                className="pay-search-item"
                                style={{
                                  cursor: already ? 'default' : 'pointer',
                                  opacity: already ? 0.5 : 1,
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  gap: 8,
                                }}
                                onClick={() => !already && addSessionAsItem(s)}
                                title={already ? 'Already added to this invoice' : 'Add this session as a line item'}
                              >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div className="pay-search-item-name" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    {s.treatmentTypeName ?? 'Session'}
                                    {already && <span style={{ fontSize: '0.65rem', color: 'var(--green-700)', fontWeight: 700 }}>· ADDED</span>}
                                  </div>
                                  <div className="pay-search-item-price">
                                    {new Date(s.scheduledAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })} · {price > 0 ? fmtINR(price) : 'no rate set'}
                                  </div>
                                </div>
                                {!already && <Plus size={14} style={{ color: 'var(--blue-700)' }} />}
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Right: invoice items / placeholder */}
                <div className="pay-items">
                  {items.length === 0 ? (
                    <div className="pay-items-empty">Create an invoice and record payment.</div>
                  ) : (
                    <>
                      {items.map((it) => {
                        const lineTotal = (Number(it.unitPrice) || 0) * (Number(it.quantity) || 0) * (1 + (Number(it.taxRate) || 0) / 100);
                        return (
                          <div key={it.tempId} className="pay-item">
                            <div className="pay-item-name">{it.description}</div>
                            <input
                              className="pay-item-input"
                              type="number" min="1" step="1"
                              value={it.quantity}
                              onChange={(e) => patchItem(it.tempId, { quantity: e.target.value })}
                              onBlur={(e) => {
                                const n = parseInt(e.target.value, 10);
                                patchItem(it.tempId, { quantity: Number.isFinite(n) && n > 0 ? n : 1 });
                              }}
                              title="Quantity"
                            />
                            <input
                              className="pay-item-input"
                              type="number" min="0" step="0.01"
                              value={it.unitPrice}
                              onChange={(e) => patchItem(it.tempId, { unitPrice: e.target.value })}
                              onBlur={(e) => {
                                const n = parseFloat(e.target.value);
                                patchItem(it.tempId, { unitPrice: Number.isFinite(n) && n >= 0 ? n : 0 });
                              }}
                              title="Unit price"
                            />
                            <div className="pay-item-total">{fmtINR(lineTotal)}</div>
                            <button className="pay-item-remove" onClick={() => removeItem(it.tempId)} title="Remove">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        );
                      })}
                      <div className="pay-items-totals">
                        <div className="pay-items-totals-row"><span>Subtotal</span><span>{fmtINR(subtotal)}</span></div>
                        {tax > 0 && (
                          <div className="pay-items-totals-row"><span>Tax</span><span>{fmtINR(tax)}</span></div>
                        )}
                        {/* Discount controls */}
                        <div className="pay-items-totals-row" style={{ alignItems: 'center', gap: 6 }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            Discount
                            <select
                              value={discountMode}
                              onChange={(e) => { setDiscountMode(e.target.value); if (e.target.value === 'none') setDiscountValue(''); }}
                              style={{ fontSize: '0.78rem', padding: '2px 4px', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)' }}
                            >
                              <option value="none">None</option>
                              <option value="percent">%</option>
                              <option value="amount">₹</option>
                            </select>
                            {discountMode !== 'none' && (
                              <input
                                type="number" min="0" step="0.01"
                                value={discountValue}
                                onChange={(e) => setDiscountValue(e.target.value)}
                                placeholder={discountMode === 'percent' ? '%' : '₹'}
                                style={{ width: 64, fontSize: '0.78rem', padding: '2px 6px', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', textAlign: 'right' }}
                              />
                            )}
                          </span>
                          <span style={{ color: discountAmount > 0 ? 'var(--red-700)' : 'var(--text-muted)' }}>
                            {discountAmount > 0 ? `– ${fmtINR(discountAmount)}` : '—'}
                          </span>
                        </div>
                        <div className="pay-items-totals-row grand"><span>Total</span><span>{fmtINR(grandTotal)}</span></div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Yellow payment band */}
            <div className="pay-band">
              <div className="pay-band-head">
                <strong>Payment</strong>
                <span className="pay-meta">(Receipt No. will be auto generated)</span>
                <span className="pay-link">edit</span>
              </div>
              <div className="pay-band-fields">
                <div className="pay-band-field">
                  <label className="pay-band-field-label">Payment Recieved (₹)</label>
                  <input
                    type="number"
                    min="0" step="1"
                    value={advance}
                    onChange={(e) => { setAdvance(e.target.value); setAdvanceTouched(true); }}
                    style={advanceError ? { borderColor: 'var(--red-500)' } : undefined}
                  />
                  {advanceError && <div className="pay-band-error">{advanceError}</div>}
                </div>
                <div className="pay-band-field">
                  <label className="pay-band-field-label">Mode of Payment <span className="req">*</span></label>
                  <select value={method} onChange={(e) => setMethod(e.target.value)}>
                    {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                  <span className="pay-band-default" onClick={handleSetDefault}>set as default</span>
                </div>
                <div className="pay-band-field">
                  <label className="pay-band-field-label">Transaction Reference</label>
                  <input
                    type="text"
                    placeholder={method === 'Upi' ? 'UPI transaction id' : method === 'Cash' ? 'Optional' : 'Receipt / txn id'}
                    value={transactionRef}
                    onChange={(e) => setTransactionRef(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* First-billing marketing attribution — mandatory on the patient's very first invoice */}
            {isFirstInvoice && (
              <div className="pay-band" style={{ background: '#eff6ff', borderColor: '#bfdbfe' }}>
                <div className="pay-band-head">
                  <strong>How did this patient find us?</strong>
                  <span className="pay-meta">Required for the first billing</span>
                </div>
                <div className="pay-band-fields">
                  <div className="pay-band-field">
                    <label className="pay-band-field-label">Marketing Source <span className="req">*</span></label>
                    <select value={marketingSource} onChange={(e) => setMarketingSource(e.target.value)}
                      style={attributionError && !marketingSource ? { borderColor: 'var(--red-500)' } : undefined}>
                      <option value="">— Select —</option>
                      {MARKETING_SOURCES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                  {marketingSource === 'Reference' && (
                    <div className="pay-band-field">
                      <label className="pay-band-field-label">Referral Name <span className="req">*</span></label>
                      <input
                        type="text"
                        placeholder="Who referred them?"
                        value={referralName}
                        onChange={(e) => setReferralName(e.target.value)}
                        style={attributionError && !referralName.trim() ? { borderColor: 'var(--red-500)' } : undefined}
                      />
                    </div>
                  )}
                  <div className="pay-band-field" style={{ justifyContent: 'flex-end' }}>
                    <span style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>
                      The initial consultation doctor is linked automatically.
                    </span>
                  </div>
                </div>
                {attributionError && <div className="pay-band-error" style={{ paddingLeft: 2 }}>{attributionError}</div>}
              </div>
            )}

            {/* Footer */}
            <div className="pay-footer">
              <button className="btn btn-outline" onClick={onClose}>Cancel</button>
              <button className="pay-accept" onClick={handleAccept} disabled={!canAccept}>
                {creating || paying ? 'Processing…' : 'Accept Payment'}
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <div className="pay-success">
            <div className="pay-success-icon"><Check size={32} /></div>
            <h3>Payment recorded</h3>
            <p>
              Invoice <strong>{paidInvoice?.invoiceNumber}</strong> created for <strong>{fmtINR(grandTotal)}</strong>.{' '}
              Payment of <strong>{fmtINR(advanceNum)}</strong> via <strong>{method}</strong> recorded successfully.
            </p>
            <div className="pay-success-actions">
              <button className="btn btn-outline" onClick={onClose}>
                <X size={14} /> Close
              </button>
              <button className="pay-accept" onClick={handlePrintReceipt} disabled={printing}>
                <Printer size={14} /> {printing ? 'Opening…' : 'Print Invoice & Receipt'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
